"use client";

import { useCallback, useEffect, useState } from "react";
import type { AnalyticsBreakdownsApiResponse, AnalyticsSummaryApiResponse, AnalyticsTrendsApiResponse } from "@/types/api";
import type { UrlFilterState } from "./use-url-filters";
import { apiFetch, buildQueryString, isAbortError, ApiClientError, type QueryParamValue } from "@/lib/api/api-client";

function buildAnalyticsQuery(filters: UrlFilterState, extras?: { mode?: "fast" }): string {
  const params: Record<string, QueryParamValue> = {
    userId: filters.userIds,
    route: filters.routes,
    method: filters.methods,
    status: filters.errorStatuses as string[] | undefined,
    statusCode: filters.statusCodes,
    responseStored: filters.responseStored,
    fixedStatus: filters.fixedStatus,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    search: filters.search,
    routeGroupingEnabled: filters.routeGroupingEnabled,
    lookback: filters.lookbackAll ? "all" : undefined,
    granularity: "day",
    mode: extras?.mode,
  };
  return buildQueryString(params);
}

function filtersKey(filters: UrlFilterState): string {
  return JSON.stringify({
    ...filters,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
  });
}

export interface UseDashboardAnalyticsResult {
  summary: AnalyticsSummaryApiResponse | null;
  trends: AnalyticsTrendsApiResponse | null;
  breakdowns: AnalyticsBreakdownsApiResponse | null;
  isSummaryLoading: boolean;
  isChartsLoading: boolean;
  error: ApiClientError | null;
  refetch: () => void;
}

/**
 * Split from the start for UX:
 * 1) Fast cards — DateIndex COUNT (total requests) paints ASAP
 * 2) Exact cards + charts — one shared full aggregate walk (in parallel)
 */
export function useDashboardAnalytics(filters: UrlFilterState): UseDashboardAnalyticsResult {
  const [summary, setSummary] = useState<AnalyticsSummaryApiResponse | null>(null);
  const [trends, setTrends] = useState<AnalyticsTrendsApiResponse | null>(null);
  const [breakdowns, setBreakdowns] = useState<AnalyticsBreakdownsApiResponse | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isChartsLoading, setIsChartsLoading] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  const key = filtersKey(filters);
  const hasDateWindow = Boolean(
    filters.createdFrom || filters.createdTo || filters.updatedFrom || filters.updatedTo,
  );

  useEffect(() => {
    if (!hasDateWindow) return;

    const controller = new AbortController();
    const { signal } = controller;
    const fastQuery = buildAnalyticsQuery(filters, { mode: "fast" });
    const fullQuery = buildAnalyticsQuery(filters);

    setIsSummaryLoading(true);
    setIsChartsLoading(true);
    setError(null);
    setSummary(null);
    setTrends(null);
    setBreakdowns(null);

    // Phase 1 + 2 start together: fast cards don't wait on the full walk.
    const fastReq = apiFetch<AnalyticsSummaryApiResponse>(`/api/analytics/summary${fastQuery}`, {
      signal,
    });
    const exactReq = Promise.all([
      apiFetch<AnalyticsSummaryApiResponse>(`/api/analytics/summary${fullQuery}`, { signal }),
      apiFetch<AnalyticsTrendsApiResponse>(`/api/analytics/trends${fullQuery}`, { signal }),
      apiFetch<AnalyticsBreakdownsApiResponse>(`/api/analytics/breakdowns${fullQuery}`, { signal }),
    ]);

    fastReq
      .then((fastSummary) => {
        if (signal.aborted) return;
        // Only apply fast payload if exact hasn't won the race yet.
        setSummary((current) => (current && !current.resultMayBePartial ? current : fastSummary));
        setIsSummaryLoading(false);
      })
      .catch((err: unknown) => {
        if (isAbortError(err) || signal.aborted) return;
        // Exact path may still succeed — don't clear the page yet.
      });

    exactReq
      .then(([exactSummary, trendsRes, breakdownsRes]) => {
        if (signal.aborted) return;
        setSummary(exactSummary);
        setIsSummaryLoading(false);
        setTrends(trendsRes);
        setBreakdowns(breakdownsRes);
        setIsChartsLoading(false);
      })
      .catch((err: unknown) => {
        if (isAbortError(err) || signal.aborted) return;
        setError(err instanceof ApiClientError ? err : new ApiClientError(500, "INTERNAL_ERROR", "Request failed"));
        setIsSummaryLoading(false);
        setIsChartsLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refetchToken, hasDateWindow]);

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), []);

  return {
    summary,
    trends,
    breakdowns,
    isSummaryLoading: hasDateWindow ? isSummaryLoading : true,
    isChartsLoading: hasDateWindow ? isChartsLoading : false,
    error,
    refetch,
  };
}
